import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// One password-gated concept brief. Everything specific to a given concept
// lives in src/concepts.js (front end) and server/<name>Content.js (copy), so
// adding a brief means adding config -- not another copy of this page.
//
// Anything that names the concept -- breadcrumb, CTA headline, body copy --
// comes from the gated payload, never from client config, so an unreleased
// concept's subject is not recoverable from the public JS bundle.

function richText(str) {
  if (!str) return null;
  return str.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

function ConceptVisual({ visual }) {
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);

  const handleReplay = () => {
    const v = videoRef.current;
    if (!v) return;
    setEnded(false);
    v.currentTime = 0;
    v.play();
  };

  if (visual.video) {
    return (
      <div className="poc-visual-wrap">
        <div className="poc-visual-caption">{visual.label}</div>
        <div className="poc-visual poc-visual-video">
          <video
            ref={videoRef}
            className="poc-video"
            src={visual.video}
            autoPlay
            muted
            playsInline
            onEnded={() => setEnded(true)}
          />
          <div className="poc-video-frame"><i></i><b></b></div>
          {ended && (
            <button type="button" className="poc-replay" onClick={handleReplay} aria-label="Replay video">
              <span className="poc-replay-icon">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="poc-visual">
      <div className="vlabel">{visual.label}</div>
      <div className="corners"><i></i><b></b></div>
      <div className="coming-soon">
        <div className="coming-soon-icon">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" />
          </svg>
        </div>
        <div className="coming-soon-text">Visual Coming Soon</div>
      </div>
      <div className="vfooter">
        <span>{visual.footerLeft}</span>
      </div>
    </div>
  );
}

// Renders an authored headline: "\n" is a line break, *text* is emphasis.
function multilineRichText(str) {
  if (!str) return null;
  return str.split('\n').map((line, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {richText(line)}
    </Fragment>
  ));
}

export default function ConceptPage({ apiKey, heroBg, idPrefix }) {
  const [phase, setPhase] = useState('checking'); // checking | locked | unlocked
  const [content, setContent] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const heroBgStyle = { backgroundImage: `url(${process.env.PUBLIC_URL}${heroBg})` };

  const loadContent = useCallback(() => {
    return fetch(`/api/${apiKey}/content`)
      .then((r) => {
        if (!r.ok) throw new Error('unauthorized');
        return r.json();
      })
      .then((data) => {
        setContent(data);
        setPhase('unlocked');
      })
      .catch(() => setPhase('locked'));
  }, [apiKey]);

  // Keyed on apiKey: each gate is independent, so moving between briefs has to
  // re-check that gate's own session rather than reuse the previous one.
  useEffect(() => {
    let cancelled = false;
    setPhase('checking');
    setContent(null);
    setPassword('');
    setError('');
    fetch(`/api/${apiKey}/session`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.authenticated) {
          loadContent();
        } else {
          setPhase('locked');
        }
      })
      .catch(() => {
        if (!cancelled) setPhase('locked');
      });
    return () => {
      cancelled = true;
    };
  }, [apiKey, loadContent]);

  function handleSubmit(ev) {
    ev.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError('');
    fetch(`/api/${apiKey}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password })
    })
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          setError(data.error || 'Incorrect password.');
          setSubmitting(false);
          return;
        }
        return loadContent().then(() => setSubmitting(false));
      })
      .catch(() => {
        setError('Something went wrong. Try again.');
        setSubmitting(false);
      });
  }

  if (phase === 'checking') {
    return (
      <section className="page-hero">
        <div className="page-hero-bg page-hero-bg-photo" style={heroBgStyle} aria-hidden="true" />
        <div className="wrap">
          <div className="mono">Checking access…</div>
        </div>
      </section>
    );
  }

  if (phase === 'locked') {
    return (
      <section className="page-hero gate-hero">
        <div className="page-hero-bg page-hero-bg-photo" style={heroBgStyle} aria-hidden="true" />
        <div className="wrap">
          <div className="crumbs">
            <Link to="/">Home</Link>
            <span className="sep">/</span>
            <span className="here">Restricted</span>
          </div>

          <div className="gate-card">
            <div className="eyebrow" style={{ marginBottom: 22 }}>RFS · Restricted Access</div>
            <h1 className="display h2" style={{ margin: '0 0 16px' }}>
              This page requires a password.
            </h1>
            <p className="body" style={{ marginBottom: 28 }}>
              Enter the access code you were given to view this concept brief.
            </p>
            <form className="gate-form" onSubmit={handleSubmit}>
              <label className="gate-label" htmlFor={`${idPrefix}-password`}>Password</label>
              <input
                id={`${idPrefix}-password`}
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="gate-input"
                placeholder="••••••••"
                disabled={submitting}
              />
              {error && <div className="gate-error">{error}</div>}
              <button type="submit" className="gate-submit" disabled={submitting || !password}>
                {submitting ? 'Checking…' : 'Unlock'}
              </button>
            </form>
            <div className="gate-hint">
              Don&apos;t have access? <Link to="/#contact">Contact Railway Flight Solutions.</Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="page-hero">
        <div className="page-hero-bg page-hero-bg-photo" style={heroBgStyle} aria-hidden="true" />
        <div className="wrap">
          <div className="crumbs">
            <Link to="/">Home</Link>
            <span className="sep">/</span>
            <span className="here">{content.crumb}</span>
          </div>

          <div className="page-hero-grid">
            <div>
              <div className="eyebrow" style={{ marginBottom: 28 }}>{content.eyebrow}</div>
              <h1 className="display h1" style={{ margin: '0 0 28px' }}>
                {richText(content.title)}
              </h1>
              <p className="lede">{content.lede}</p>
            </div>

            <aside className="page-meta" aria-label="At a glance">
              {content.meta.map((row) => (
                <div className="page-meta-row" key={row.k}>
                  <span className="k">{row.k}</span>
                  <span className="v">{row.v}</span>
                </div>
              ))}
            </aside>
          </div>
        </div>
      </section>

      <section className="poc">
        {content.sections.map((s, i) => (
          <article key={s.id} id={s.id} className={`poc-item${i % 2 === 1 ? ' reverse' : ''}`}>
            <div className="wrap">
              <div className="poc-grid">
                <div className="poc-text">
                  <div className="poc-cat-row">
                    <span className="poc-cat-num">{s.num} —</span>
                    <h2 className="poc-cat">{s.cat}</h2>
                  </div>
                  <div className="poc-tagline">{s.tagline}</div>
                  <h3 className="poc-title">{richText(s.title)}</h3>
                  <p className="poc-lede">{s.lede}</p>
                  <ul className="poc-list">
                    {s.bullets.map((b, idx) => (
                      <li key={idx}>
                        <span className="marker">{String(idx + 1).padStart(2, '0')}</span>
                        <span className="label">{richText(b)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <ConceptVisual visual={s.visual} />
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="cta-band">
        <div className="cta-band-bg" aria-hidden="true" />
        <div className="wrap">
          <div className="cta-band-grid">
            <h2 className="cta-title">{multilineRichText(content.ctaTitle)}</h2>
            <Link to="/#contact" className="cta-link">
              <span>Contact Railway Flight Solutions</span>
              <span className="arrow">→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
